import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faEdit, 
  faTrash, 
  faPlus, 
  faChevronLeft, 
  faChevronRight, 
  faSpinner, 
  faExclamationTriangle,
  faCrown,
  faLock,
  faCog,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import { doc, updateDoc, getDoc, writeBatch, getDocs, deleteDoc, collection, query, where, orderBy, limit } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './Crew.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import { useNavigate } from 'react-router-dom';
import AuthCheckWrapper from '../AuthCheckWrapper';

// Permission Settings Modal component
const PermissionSettingsModal = ({ show, onClose, onSave, member, currentUserPermissions }) => {
  const [permissions, setPermissions] = useState(member?.permissions || {});
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Update permissions when member changes
  useEffect(() => {
    if (member && show) {
      setPermissions(member.permissions || {});
    }
  }, [member, show]);

  if (!show) return null;

  // Permission groups
  const permissionGroups = [
    {
      name: "Tour Management",
      permissions: [
        { key: "canEditTourDetails", label: "Edit Tour Details" },
        { key: "canAssignUsers", label: "Assign Users" }
      ]
    },
    {
      name: "Content Management",
      permissions: [
        { key: "canEditSchedule", label: "Edit Schedule" },
        { key: "canAddAttachments", label: "Add Attachments" },
        { key: "canEditSetlist", label: "Edit Setlist" }
      ]
    },
    {
      name: "Task Management",
      permissions: [
        { key: "canAssignTasks", label: "Assign Tasks" }
      ]
    },
    {
      name: "Event Management",
      permissions: [
        { key: "canManageGuestlist", label: "Manage Guestlist" },
        { key: "canManageHotels", label: "Manage Hotels" },
        { key: "canManageTickets", label: "Manage Tickets" }
      ]
    }
  ];

  // Preset permission levels
  const permissionLevels = {
    fullControl: {
      canEditTourDetails: true,
      canEditSchedule: true,
      canAddAttachments: true,
      canAssignTasks: true,
      canEditSetlist: true,
      canManageGuestlist: true,
      canManageHotels: true,
      canManageTickets: true,
      canAssignUsers: true
    },
    viewOnly: {
      canEditTourDetails: false,
      canEditSchedule: false,
      canAddAttachments: false,
      canAssignTasks: false,
      canEditSetlist: false,
      canManageGuestlist: false,
      canManageHotels: false,
      canManageTickets: false,
      canAssignUsers: false
    },
    collab: {
      canEditTourDetails: false,
      canEditSchedule: true,
      canAddAttachments: true,
      canAssignTasks: true,
      canEditSetlist: true,
      canManageGuestlist: true,
      canManageHotels: true,
      canManageTickets: true,
      canAssignUsers: false
    }
  };

  // Determine current permission level based on settings
  const getCurrentPermissionLevel = () => {
    if (JSON.stringify(permissions) === JSON.stringify(permissionLevels.fullControl)) {
      return 'fullControl';
    } else if (JSON.stringify(permissions) === JSON.stringify(permissionLevels.viewOnly)) {
      return 'viewOnly';
    } else if (JSON.stringify(permissions) === JSON.stringify(permissionLevels.collab)) {
      return 'collab';
    } else {
      return 'custom';
    }
  };

  const handleTogglePermission = (key) => {
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSetPermissionLevel = (level) => {
    setPermissions(permissionLevels[level]);
  };

  const handleSave = async () => {
    setIsProcessing(true);
    try {
      await onSave(member.userId, permissions);
      onClose();
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('Failed to update permissions. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const currentLevel = getCurrentPermissionLevel();

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Manage Permissions for {member.fullName}</h3>
        
        <div className={styles.permissionLevels}>
          <button 
            className={`${styles.permissionLevelButton} ${currentLevel === 'fullControl' ? styles.active : ''}`}
            onClick={() => handleSetPermissionLevel('fullControl')}
          >
            <FontAwesomeIcon icon={faUserShield} />
            <span>Full Control</span>
          </button>
          <button 
            className={`${styles.permissionLevelButton} ${currentLevel === 'viewOnly' ? styles.active : ''}`}
            onClick={() => handleSetPermissionLevel('viewOnly')}
          >
            <FontAwesomeIcon icon={faLock} />
            <span>View Only</span>
          </button>
          <button 
            className={`${styles.permissionLevelButton} ${currentLevel === 'collab' ? styles.active : ''}`}
            onClick={() => handleSetPermissionLevel('collab')}
          >
            <FontAwesomeIcon icon={faUsers} />
            <span>Collaboration</span>
          </button>
        </div>
        
        <div className={styles.permissionsContainer}>
          {permissionGroups.map(group => (
            <div key={group.name} className={styles.permissionGroup}>
              <h4 className={styles.permissionGroupTitle}>{group.name}</h4>
              <div className={styles.permissionItems}>
                {group.permissions.map(permission => (
                  <div key={permission.key} className={styles.permissionItem}>
                    <span className={styles.permissionLabel}>{permission.label}</span>
                    <div 
                      className={`${styles.permissionToggle} ${permissions[permission.key] ? styles.active : ''}`}
                      onClick={() => handleTogglePermission(permission.key)}
                    >
                      <div className={styles.toggleSlider}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className={styles.modalActions}>
          <button 
            className={styles.modalCancelButton} 
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button 
            className={styles.modalSaveButton} 
            onClick={handleSave}
            disabled={isProcessing}
          >
            {isProcessing ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({ show, onConfirm, onCancel, memberName }) => {
  if (!show) return null;

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Remove Member</h3>
        <p className={styles.modalText}>
          Are you sure you want to remove {memberName} from this date?
        </p>
        <div className={styles.modalActions}>
          <button className={styles.modalCancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button className={styles.modalConfirmButton} onClick={onConfirm}>
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

const Crew = () => {
  const { 
    selectedEvent, 
    selectedDate, 
    setSelectedDate, 
    updateEventData 
  } = useContext(TourContext);
  const navigate = useNavigate();
  const { userData, getUserDataFromCache, getProfileData } = useContext(UserContext);
  const [crewMembers, setCrewMembers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [manager, setManager] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [dateScrollPosition, setDateScrollPosition] = useState(0);
  const [pendingArtists, setPendingArtists] = useState([]);
  const [pendingCrew, setPendingCrew] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [creatorId, setCreatorId] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [memberForPermissions, setMemberForPermissions] = useState(null);
  const [currentUserPermissions, setCurrentUserPermissions] = useState({
    canAssignUsers: false
  });

  const handleMemberClick = (userName) => {
    navigate(`/${userName}`);
  };
  
  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const handleManagePermissions = (member) => {
    setMemberForPermissions(member);
    setShowPermissionModal(true);
  };

  const handleDeleteMember = (userId) => {
    const member = [...artists, ...crewMembers].find(m => m.userId === userId);
      
    if (member) {
      setMemberToDelete(member);
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = async () => {
    if (!memberToDelete || !selectedDate) return;
  
    try {
      const db = getFirestoreInstance();
      const eventType = selectedEvent.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete from the date's assignedUsers subcollection
      const userRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'assignedUsers', memberToDelete.userId);
      await deleteDoc(userRef);
      
      // Update state to reflect changes
      if (memberToDelete.userType === 'Artist') {
        setArtists(prevArtists => prevArtists.filter(artist => artist.userId !== memberToDelete.userId));
      } else if (memberToDelete.userType === 'Crew') {
        setCrewMembers(prevCrew => prevCrew.filter(crew => crew.userId !== memberToDelete.userId));
      }
      
      // Use the updateTourData from context to refresh the tour data
      await updateEventData(selectedEvent.id, selectedEvent.type);
  
    } catch (error) {
      console.error('Error removing member:', error);
    } finally {
      setShowDeleteModal(false);
      setMemberToDelete(null);
    }
  };

  // Update permissions in both collections
  const updateMemberPermissions = async (userId, newPermissions) => {
    try {
      const db = getFirestoreInstance();
      const eventType = selectedEvent.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const batch = writeBatch(db);
      
      // 1. Update permissions in the event date's assignedUsers subcollection
      const assignedUserRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'assignedUsers', userId);
      batch.update(assignedUserRef, {
        permissions: newPermissions
      });
      
      // 2. Update permissions in user's assignedEvents subcollection
      const userEventRef = doc(db, 'users', userId, 'assignedEvents', selectedEvent.id);
      batch.update(userEventRef, {
        permissions: newPermissions
      });
      
      await batch.commit();
      
      // 3. Update local state to reflect changes
      setArtists(prevArtists => 
        prevArtists.map(artist => 
          artist.userId === userId ? { ...artist, permissions: newPermissions } : artist
        )
      );
      
      setCrewMembers(prevCrew => 
        prevCrew.map(crew => 
          crew.userId === userId ? { ...crew, permissions: newPermissions } : crew
        )
      );
      
      if (manager && manager.userId === userId) {
        setManager({ ...manager, permissions: newPermissions });
      }
      
      // 4. Refresh event data
      await updateEventData(selectedEvent.id, selectedEvent.type);
      
      return true;
    } catch (error) {
      console.error('Error updating permissions:', error);
      throw error;
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle date scroll
  const handleScroll = (direction) => {
    const scrollContainer = document.getElementById('dateScroller');
    if (scrollContainer) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date.id);
  };

  // Determine if current user can manage permissions
  const userCanManagePermissions = () => {
    // Creator can always manage permissions
    if (userData.userId === creatorId) return true;
    
    // Check if user has permission to assign users
    return currentUserPermissions.canAssignUsers;
  };
  
  useEffect(() => {
    let isMounted = true; // Flag to track if component is still mounted
    
    const fetchData = async () => {
      if (!selectedEvent || !selectedDate) return;
      
      // Set loading state at the beginning
      if (isMounted) setIsLoading(true);
      
      try {
        const eventDate = selectedEvent.eventDates?.find(date => date.id === selectedDate);
        const actualDate = eventDate?.date;
        
        // Reset all state values at once to prevent multiple renders
        if (isMounted) {
          setManager(null);
          setArtists([]);
          setCrewMembers([]);
          setPendingArtists([]);
          setPendingCrew([]);
        }
        
        // Store creator ID
        if (selectedEvent.createdBy?.userId && isMounted) {
          setCreatorId(selectedEvent.createdBy.userId);
        }
        
        // Get current user's permissions
        if (userData.userId && selectedEvent.id) {
          try {
            const db = getFirestoreInstance();
            const eventType = selectedEvent.type || 'tour';
            const collectionName = eventType === 'tour' ? 'tours' : 'shows';
            
            // First check if user is the creator
            if (selectedEvent.createdBy?.userId === userData.userId) {
              setCurrentUserPermissions({
                canAssignUsers: true
              });
            } else {
              // Check assignedUsers subcollection in the specific eventDate
              const assignedUserDoc = await getDoc(doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'assignedUsers', userData.userId));
              
              if (assignedUserDoc.exists()) {
                const assignedUserData = assignedUserDoc.data();
                if (assignedUserData.permissions) {
                  setCurrentUserPermissions({
                    canAssignUsers: assignedUserData.permissions.canAssignUsers || false
                  });
                }
              }
            }
          } catch (error) {
            console.error('Error fetching user permissions:', error);
          }
        }
        
        const db = getFirestoreInstance();
        const eventType = selectedEvent.type || 'tour';
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Query the assignedUsers subcollection of the specific date
        const assignedUsersRef = collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'assignedUsers');
        const assignedUsersSnapshot = await getDocs(assignedUsersRef);
        
        // Process artists, crew and manager from assignedUsers
        const hydratedArtists = [];
        const hydratedCrew = [];
        let managerData = null;
        
        // Process each user in the assignedUsers collection
        for (const userDoc of assignedUsersSnapshot.docs) {
          const userData = userDoc.data();
          
          // Skip if missing required fields
          if (!userData.userId) continue;
          
          // Get the user's profile data
          let profileData = null;
          try {
            profileData = await getProfileData(userData.userId);
          } catch (error) {
            console.error(`Error fetching profile data for ${userData.userId}: ${error.message}`);
          }
          
          // Create the base user object
          const userObj = {
            userId: userData.userId,
            fullName: userData.fullName || (profileData?.fullName || ''),
            userName: userData.userName || (profileData?.userName || ''),
            profilePictureUrl: userData.profilePictureUrl || (profileData?.profilePictureUrl || ''),
            userType: userData.userType || (userData.userDetails?.userType || ''),
            userSubType: userData.userSubType || (userData.userDetails?.userSubType || ''),
            idVerificationStatus: userData.idVerificationStatus || (userData.userDetails?.idVerificationStatus || 'Unverified'),
            isCreator: userData.isCreator || false,
            permissions: userData.permissions || {},
            role: userData.role || ''
          };
          
          // Categorize by userType
          const userType = userObj.userType || (userData.role ? userData.role.charAt(0).toUpperCase() + userData.role.slice(1) : '');
          
          if (userType === 'Artist') {
            hydratedArtists.push(userObj);
          } else if (userType === 'Crew') {
            hydratedCrew.push(userObj);
          } else if (userType === 'Manager' || userData.role === 'manager') {
            managerData = userObj;
          }
        }
        
        // Fallback to createdBy if no manager is assigned
        if (!managerData && selectedEvent.createdBy && isMounted) {
          try {
            const creatorData = await getProfileData(selectedEvent.createdBy.userId);
            if (creatorData && isMounted) {
              managerData = {
                userId: creatorData.userId,
                fullName: creatorData.fullName,
                userName: creatorData.userName,
                profilePictureUrl: creatorData.profilePictureUrl,
                userType: 'Manager',
                userSubType: creatorData.userSubType || '',
                idVerificationStatus: creatorData.idVerificationStatus || 'Unverified',
                permissions: { canAssignUsers: true }, // Creator has full permissions
                isCreator: true,
                role: 'manager'
              };
            }
          } catch (error) {
            console.error(`Error fetching creator data: ${error.message}`);
          }
        }
        
        // Query pendingInvitations for this specific date
        const pendingInvitationsRef = collection(db, collectionName, selectedEvent.id, 'pendingInvitations');
        const pendingInvitationsSnapshot = await getDocs(pendingInvitationsRef);
        
        // Process pending invitations
        const pendingArtistsData = [];
        const pendingCrewData = [];
        
        for (const invitationDoc of pendingInvitationsSnapshot.docs) {
          const invitation = invitationDoc.data();
          
          // Check if this invitation includes the current date
          const includesCurrentDate = invitation.assignedDates?.some(d => 
            d.dateDocId === selectedDate
          );
          
          if (includesCurrentDate && invitation.status === 'pending') {
            const invitationObj = {
              userId: invitation.userId,
              fullName: invitation.fullName || '',
              userName: invitation.userName || '',
              profilePictureUrl: invitation.profilePictureUrl || '',
              userType: invitation.userType || '',
              userSubType: invitation.userSubType || '',
              idVerificationStatus: invitation.idVerificationStatus || 'Unverified',
              role: invitation.role || '',
              permissions: invitation.permissions || {}
            };
            
            if (invitationObj.userType === 'Artist' || invitation.role === 'Artist') {
              pendingArtistsData.push(invitationObj);
            } else if (invitationObj.userType === 'Crew' || invitation.role === 'Crew') {
              pendingCrewData.push(invitationObj);
            }
          }
        }
        
        // Update state if component is still mounted
        if (isMounted) {
          if (managerData) setManager(managerData);
          setArtists(hydratedArtists);
          setCrewMembers(hydratedCrew);
          setPendingArtists(pendingArtistsData);
          setPendingCrew(pendingCrewData);
        }
        
      } catch (error) {
        console.error(`Error in Crew data fetching: ${error.message}`);
      } finally {
        // Set loading to false only if component is still mounted
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    // Call the async function
    fetchData();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [selectedEvent, selectedDate, getProfileData, userData.userId]);

  const MemberSection = ({ title, members, pendingMembers }) => {
    if (!members?.length && !pendingMembers?.length) {
      return null;
    }
  
    return (
      <div className={styles['member-section']}>
        <h2 className={styles['section-title']}>{title}</h2>
        <div className={styles['members-list']}>
          {/* Regular Members */}
          {members?.map((member) => (
          <div 
            key={member.userId} 
            className={styles['member-card']}
            onClick={() => navigate(`/${member.userName}`)}
            style={{ cursor: 'pointer' }}
          >
              <div className={styles['member-header']}>
                <img
                  src={member.profilePictureUrl || defaultProfilePhoto}
                  alt={member.fullName}
                  className={styles['member-photo']}
                />
                <div className={styles['member-info']}>
                  <div className={styles['name-container']}>
                    <h3>{member.fullName}</h3>
                    {member.idVerificationStatus === 'Verified' && (
                      <img src={verifiedBadge} alt="Verified" className={styles['verified-badge']} />
                    )}
                    {member.isCreator && (
                      <span className={styles['creator-badge']}>
                        <FontAwesomeIcon icon={faCrown} title="Creator" />
                      </span>
                    )}
                  </div>
                  <p className={styles['member-username']}>@{member.userName}</p>
                  <p className={styles['member-type']}>{member.userSubType}</p>
                </div>
                {(userCanManagePermissions() || userData.userId === member.userId) && (
                  <div 
                    className={styles['member-actions']}
                    onClick={(e) => e.stopPropagation()} 
                  >
                    {userCanManagePermissions() && (
                      <button 
                        className={styles['action-button']}
                        onClick={() => handleManagePermissions(member)}
                        title="Manage Permissions"
                      >
                        <FontAwesomeIcon icon={faCog} />
                      </button>
                    )}
                    {userCanManagePermissions() && member.userId !== creatorId && (
                      <button 
                        className={styles['action-button']}
                        onClick={() => handleDeleteMember(member.userId)}
                        title="Remove Member"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
  
          {/* Pending Members */}
          {pendingMembers?.map((member) => (
          <div 
            key={`pending-${member.userId}`}
            className={`${styles['member-card']} ${styles['pending-member']}`}
            onClick={() => navigate(`/${member.userName}`)}
            style={{ cursor: 'pointer' }}
          >
              <div className={styles['member-header']}>
                <img
                  src={member.profilePictureUrl || defaultProfilePhoto}
                  alt={member.fullName}
                  className={styles['member-photo']}
                />
                <div className={styles['member-info']}>
                  <div className={styles['name-container']}>
                    <h3>{member.fullName}</h3>
                    {member.idVerificationStatus === 'Verified' && (
                      <img src={verifiedBadge} alt="Verified" className={styles['verified-badge']} />
                    )}
                  </div>
                  <p className={styles['member-username']}>@{member.userName}</p>
                  <p className={styles['member-type']}>{member.userSubType}</p>
                  <span className={styles['pending-badge']}>Pending Invitation</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles['main-layout']}>
     
      <LeftMenu />
      <div className={styles['crew-main']}>
        <AuthCheckWrapper>
          {isLoading ? (
            <div className={styles['loading-container']}>
              <div className={styles['loading-spinner']}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
              <p className={styles['loading-text']}>Loading crew data...</p>
            </div>
          ) : (
            <div className={styles['crew-content']}>
              {/* Date Selector */}
              <div className={styles['date-selector']}>
                <button 
                  className={styles['scroll-button']} 
                  onClick={() => handleScroll('left')}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                
                <div className={styles['dates-container']} id="dateScroller">
                  {selectedEvent?.eventDates?.map((date) => {
                    const isRescheduled = date.rescheduleDate?.isRescheduled;
                    const rescheduledDate = isRescheduled ? new Date(date.rescheduleDate.newDate) : null;
                    const isCancelled = date.cancelled;
                    
                    // Display rescheduled date if available, otherwise show original date
                    const displayDate = isRescheduled ? rescheduledDate : new Date(date.date);
    
                    return (
                      <button
                        key={date.id}
                        className={`${styles['date-button']} 
                          ${selectedDate === date.id ? styles['selected'] : ''}
                          ${isCancelled ? styles['cancelled'] : ''}
                          ${isRescheduled ? styles['rescheduled'] : ''}`}
                        onClick={() => handleDateSelect(date)}
                      >
                        <div className={styles['date-content']}>
                          <span className={styles['date-day']}>
                            {formatDate(displayDate)}
                          </span>
                          {date.event?.venueDetails?.venueData && (
                            <span className={styles['date-venue']}>
                              {date.event.venueDetails.venueData.displayName}
                            </span>
                          )}
                        </div>
                        <div className={styles['status-badges']}>
                          {isCancelled && (
                            <span className={styles['cancelled-badge']}>Cancelled</span>
                          )}
                          {isRescheduled && (
                            <span className={styles['rescheduled-badge']}>Rescheduled</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>            
                <button 
                  className={styles['scroll-button']} 
                  onClick={() => handleScroll('right')}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
    
              {/* Manager Section */}
              {manager && <MemberSection title="Manager" members={[manager]} pendingMembers={[]} />}
    
              {/* Artists Section */}
              {(artists.length > 0 || pendingArtists.length > 0) ? (
                <MemberSection 
                  title="Artists" 
                  members={artists} 
                  pendingMembers={pendingArtists}
                />
              ) : (
                <div className={styles['empty-state']}>
                  <FontAwesomeIcon icon={faUsers} className={styles['empty-icon']} />
                  <p className={styles['empty-text']}>No Artist added for this date</p>
                </div>
              )}
    
              {/* Crew Section */}
              {(crewMembers.length > 0 || pendingCrew.length > 0) ? (
                <MemberSection 
                  title="Crew" 
                  members={crewMembers} 
                  pendingMembers={pendingCrew}
                />
              ) : (
                <div className={styles['empty-state']}>
                  <FontAwesomeIcon icon={faUsers} className={styles['empty-icon']} />
                  <p className={styles['empty-text']}>No crew members added for this date</p>
                </div>
              )}
            </div>
          )}
        </AuthCheckWrapper>
      </div>
  
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setMemberToDelete(null);
        }}
        memberName={memberToDelete?.fullName}
      />
      
      {/* Permission Settings Modal */}
      <PermissionSettingsModal
        show={showPermissionModal}
        onClose={() => {
          setShowPermissionModal(false);
          setMemberForPermissions(null);
        }}
        onSave={updateMemberPermissions}
        member={memberForPermissions}
        currentUserPermissions={currentUserPermissions}
      />
  
      <RightMenu />
    </div>
  );
};

export default Crew;